// DispatchDeck — Mode A GPS Ping Utility
// Event-based location capture for slip creation, arrive/depart, clock-in/out.
// Foreground-only (no background tracking). Always resolves; never throws.

import {
  db,
  collection,
  addDoc,
  Timestamp
} from '../firebase-config.js?v=1868';

const PING_TIMEOUT_MS = 7000;
const MAX_AGE_MS = 30000;
const PREAMBLE_LS_KEY = 'dd_gps_preamble_seen_v1';

// ============================================
// SOFT PREAMBLE — shown once per device before the first native GPS prompt.
// Drivers will reflexively hit Deny on iOS Safari / Android Chrome if the
// native dialog appears unexpectedly. The preamble explains *why* the app
// is asking, and once the user dismisses it the LS flag persists so we
// don't show it again. Cheap insurance.
// ============================================
async function showGpsPreambleIfNeeded() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    if (localStorage.getItem(PREAMBLE_LS_KEY) === '1') return;
  } catch (_) { /* localStorage may be unavailable in privacy mode */ }

  // If the browser already has a Permissions API result (granted/denied), skip
  // the preamble — the user has already decided about this site.
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'granted' || status.state === 'denied') {
        try { localStorage.setItem(PREAMBLE_LS_KEY, '1'); } catch (_) {}
        return;
      }
    }
  } catch (_) { /* not all browsers support Permissions API for geolocation */ }

  return new Promise((resolve) => {
    const wrap = document.createElement('div');
    wrap.className = 'modal';
    wrap.id = 'gps-preamble-modal';
    wrap.style.cssText = 'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,0.65);';
    wrap.innerHTML = `
      <div style="background:var(--bg-primary,#fff);max-width:420px;width:90%;border-radius:12px;padding:24px;box-shadow:0 12px 40px rgba(0,0,0,0.25);">
        <div style="display:flex;justify-content:center;margin-bottom:16px;">
          <div style="width:64px;height:64px;border-radius:50%;background:#3B82F6;display:flex;align-items:center;justify-content:center;">
            <span class="material-icons" style="color:#fff;font-size:32px;">location_on</span>
          </div>
        </div>
        <h3 style="margin:0 0 12px;text-align:center;font-size:1.15rem;color:var(--text-primary,#0F172A);">Location access</h3>
        <p style="margin:0 0 16px;color:var(--text-secondary,#475569);font-size:0.95rem;line-height:1.45;text-align:center;">
          Your company stamps work events with GPS for proof of presence
          (clock-in, clock-out, ship slips). On the next screen your browser
          will ask to share your location — please tap <strong>Allow</strong>.
        </p>
        <p style="margin:0 0 20px;color:var(--text-muted,#64748B);font-size:0.8rem;line-height:1.4;text-align:center;">
          DispatchDeck only captures location at specific work events — it does
          not track you continuously.
        </p>
        <button id="gps-preamble-ok" style="width:100%;padding:14px;border:0;border-radius:8px;background:#3B82F6;color:#fff;font-size:1rem;font-weight:600;cursor:pointer;min-height:48px;">Continue</button>
      </div>
    `;
    document.body.appendChild(wrap);
    const cleanup = () => {
      try { localStorage.setItem(PREAMBLE_LS_KEY, '1'); } catch (_) {}
      wrap.remove();
      resolve();
    };
    wrap.querySelector('#gps-preamble-ok').addEventListener('click', cleanup);
    // No auto-dismiss — driver must explicitly acknowledge before native prompt fires.
  });
}

function getCurrentPositionSafe() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return resolve({ ok: false, reason: 'unsupported' });
    }
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) { settled = true; resolve({ ok: false, reason: 'timeout' }); }
    }, PING_TIMEOUT_MS + 1000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        const c = position.coords;
        resolve({
          ok: true,
          lat: c.latitude,
          lng: c.longitude,
          accuracy: c.accuracy,
          altitude: c.altitude ?? null,
          heading: c.heading ?? null,
          speed: c.speed ?? null
        });
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        const reason = err?.code === 1 ? 'permission_denied'
          : err?.code === 2 ? 'position_unavailable'
          : err?.code === 3 ? 'timeout'
          : 'error';
        console.warn('[gpsPing] geolocation error:', reason, err?.message);
        resolve({ ok: false, reason });
      },
      { enableHighAccuracy: true, timeout: PING_TIMEOUT_MS, maximumAge: MAX_AGE_MS }
    );
  });
}

/**
 * Capture an event-based GPS ping and write it to tenants/{tenant}/pings.
 *
 * @param {Object} opts
 * @param {string} opts.eventType  — one of: slip_created, arrive_site, depart_site, clock_in, clock_out
 * @param {string} [opts.tenant]   — tenant ID (defaults to AppState.tenant)
 * @param {string} [opts.ticketId] — parent ticket reference
 * @param {string} [opts.slipId]   — slip reference (when eventType=slip_created)
 * @param {string} [opts.clockEntryId] — clock entry reference (when eventType=clock_in/out)
 * @param {string} [opts.driverId]
 * @param {string} [opts.driverName]
 * @param {Object} [opts.extra]    — any additional metadata
 * @returns {Promise<{ok: boolean, pingId?: string, reason?: string}>}
 */
export async function capturePing(opts = {}) {
  const tenant = opts.tenant || window.AppState?.tenant;
  if (!tenant) return { ok: false, reason: 'no_tenant' };
  if (!opts.eventType) return { ok: false, reason: 'no_event_type' };

  // Show one-time soft preamble before the first native location prompt.
  // No-op if already shown / permission already decided.
  await showGpsPreambleIfNeeded();

  const fix = await getCurrentPositionSafe();

  const pingDoc = {
    eventType: opts.eventType,
    ticketId: opts.ticketId || null,
    slipId: opts.slipId || null,
    clockEntryId: opts.clockEntryId || null,
    driverId: opts.driverId || null,
    driverName: opts.driverName || null,
    user: window.AppState?.user?.email || window.AppState?.driverPinSession?.driverName || 'unknown',
    timestamp: Timestamp.now(),
    clientTime: Date.now(),
    gpsAvailable: fix.ok,
    ...(fix.ok ? {
      lat: fix.lat,
      lng: fix.lng,
      accuracy: fix.accuracy,
      altitude: fix.altitude,
      heading: fix.heading,
      speed: fix.speed
    } : {
      gpsFailReason: fix.reason
    }),
    ...(opts.extra || {})
  };

  try {
    const ref = await addDoc(collection(db, 'tenants', tenant, 'pings'), pingDoc);
    return { ok: true, pingId: ref.id, gpsAvailable: fix.ok, reason: fix.ok ? null : fix.reason };
  } catch (err) {
    console.warn('[gpsPing] write failed:', err);
    return { ok: false, reason: 'write_failed' };
  }
}

/**
 * Convenience: only captures a ping if a tenant setting is true.
 * Used by clock-in/out where the toggle gates the behavior.
 */
export async function capturePingIfEnabled(settingKey, opts = {}) {
  const settings = window.AppState?.settings || {};
  if (settings[settingKey] !== true) return { ok: false, reason: 'setting_disabled' };
  return capturePing(opts);
}

// Belt-and-suspenders: also expose on window so callers can use either pattern.
// Prevents duplicate-instance bugs if another module ever imports this with a
// mismatched ?v= cache buster (per CLAUDE.md "no cross-module versioned imports" rule).
if (typeof window !== 'undefined') {
  window.GpsPing = { capturePing, capturePingIfEnabled };
}
